<template>
  <aside class="app-sidebar">
    <div class="app-sidebar__inner">
      <div class="app-sidebar__header">
        <div class="app-sidebar__brand">
          <h2 class="app-sidebar__brand-title">Jij bent</h2>
          <h1 class="app-sidebar__brand-title">PRO</h1>
          <Logo
            class="app-sidebar__brand-logo"
            alt="Beeldmerk van Progressief Nederland"
            aria-hidden="true"
          />
        </div>
        <p>Upload jouw eigen foto en stel drie unieke thema’s in met een eigen kleur.</p>
      </div>

      <SidebarForm/>
    </div>
  </aside>

  <main class="app-main">
    <FabricCanvasEditor/>
  </main>
</template>

<script setup lang="ts">
  import Logo from '@/components/svg/Logo.vue';
  import FabricCanvasEditor from '@/components/FabricCanvasEditor.vue';
  import SidebarForm from '@/components/SidebarForm.vue';
  import { useVisualViewportHeight } from '@/composables/useVisualViewportHeight';

  // Sets --vvh CSS variable.
  useVisualViewportHeight();
</script>

<style lang="scss">
  #app {
    background-color: var(--color-background);

    @include desktop-view {
      display: grid;
      grid-template-columns: clamp(380px, 30%, 460px) minmax(0, 1fr);
      align-items: start;
    }
  }

  .app-sidebar {
    position: relative;
    z-index: 2;
    min-height: 100svh;
    overflow: visible;
    background: color-opacity(--color-white, 82%);
    border-right: 1px solid color-opacity(--color-black, 8%);
    box-shadow: 12px 0 40px color-opacity(--color-black, 3%);

    @include mobile-view {
      min-height: 0;
      border-right: 0;
      border-bottom: 1px solid color-opacity(--color-black, 8%);
      box-shadow: 0 10px 35px color-opacity(--color-black, 3%);
    }

    &__inner {
      --padding-top: 42px;
      --padding-sides: 28px;
      --padding-bottom: 42px;

      min-height: 100%;
      padding: var(--padding-top) var(--padding-sides) var(--padding-bottom);
      display: flex;
      flex-direction: column;
      gap: 24px;

      @include mobile-view {
        --padding-top: 32px;
        --padding-sides: 21px;
        --padding-bottom: 32px;
      }
    }

    &__header {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .app-sidebar__brand {
        align-self: flex-start;
        display: grid;
        grid-template-columns: min-content auto;
        grid-template-rows: min-content min-content;
        place-items: center;
        column-gap: 0.2em;
        margin: calc(-1 * var(--padding-top)) calc(-1 * var(--padding-sides)) 0;
        padding: var(--padding-top) var(--padding-sides);
        font-family: var(--ui-headings-font-family);
        font-weight: var(--ui-headings-font-weight);
        font-size: clamp(36px, 4.2vw, 42px);
        color: var(--color-primair-rood);
        line-height: 0.85;
        position: relative;

        // Colored triangle
        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          aspect-ratio: 1;
          background-color: var(--color-lichtgroen);
          clip-path: polygon(0 0, 100% 0, 0 100%);
          z-index: -1;
          pointer-events: none;
        }

        &-title {
          font-size: inherit;
          margin: 0;
          letter-spacing: -0.01em;
          text-transform: uppercase;

          &:is(h2) {
            grid-column: 1 / -1;
          }

          &:is(h1) {
            font-size: 155%;
          }
        }

        &-logo {
          display: inline-block;
          height: 1lh;
          width: auto;
          scale: 1.2;
          translate: 0 0.08em;
          color: var(--color-primair-groen);
        }
      }

      p {
        margin: 0;
        font-size: 18px;
        line-height: 1.45;
        text-wrap: pretty;
      }
    }
  }

  .app-main {
    min-width: 0;
    min-height: 0;
    height: 100svh;
    position: sticky;
    top: 0;
    align-self: start;
    overflow: hidden;
    display: flex;
    align-items: stretch;
    justify-content: stretch;

    @include mobile-view {
      position: relative;
      height: auto;
      align-self: stretch;
    }
  }
</style>
